import { Boxes, Palette } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getTheme, type Theme } from '../themes';
import { themeAssetExists } from '../themeAssets';
import { useThemeSelection } from '../useThemeSelection';
import { useThemeLineup } from '../themeLineup';
import { ThemeThumbnail } from './ThemeThumbnail';

interface ThemeSwitcherProps {
  className?: string;
  /**
   * Opens the full Locker (themes + titles + emblems). Wired by the host (the
   * avatar menu) so the theme feature never has to depend on the profile
   * feature — keeps the dependency one-directional.
   */
  onOpenLocker?: () => void;
}

/**
 * Compact theme control for the avatar menu: a clean "Theme" title (no active
 * name text) and the user's quick-switch lineup — up to 8 themes, 4 across.
 * Which 8 appear is curated in the Locker (persisted via `useThemeLineup`), so
 * this stays small while the full collection lives one click away.
 *
 * The accent color is fixed (electric blue) and intentionally not editable here.
 */
export function ThemeSwitcher({ className, onOpenLocker }: ThemeSwitcherProps) {
  const { activeId, pick } = useThemeSelection();
  const lineup = useThemeLineup((s) => s.lineup);
  const themes = lineup
    .map((id) => getTheme(id))
    .filter((t): t is Theme => Boolean(t));

  return (
    <div className={cn('px-3 pb-2.5 pt-2', className)}>
      <div className="mb-2 px-0.5">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.4px] text-hi">
          <Palette size={13} strokeWidth={2} />
          Theme
        </div>
        <div className="mt-1 text-[11.5px] font-semibold leading-[1.35] text-white/55">
          Add up to 8 or switch them around in Locker.
        </div>
      </div>

      {/* Your lineup — 8 quick picks, 4 across. Curate the set in the Locker. */}
      <div className="grid grid-cols-4 gap-1.5">
        {themes.map((t) => {
          const unavailable = t.file != null && !themeAssetExists(t.file);
          return (
            <button
              key={t.id}
              type="button"
              title={unavailable ? `${t.name} — image not added yet` : t.name}
              aria-label={t.name}
              aria-pressed={t.id === activeId}
              disabled={unavailable}
              onClick={() => pick(t.id)}
              className={cn(
                'aspect-square overflow-hidden rounded-md border-2 transition-transform',
                unavailable ? 'cursor-not-allowed opacity-40' : 'hover:scale-105 active:scale-95',
              )}
              style={{ borderColor: t.id === activeId ? 'var(--winr-accent)' : 'transparent' }}
            >
              <ThemeThumbnail theme={t} />
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onOpenLocker}
        className="ml-0.5 mt-3 flex w-[calc(100%-0.125rem)] items-center gap-3 rounded-lg bg-transparent px-2.5 py-2.5 text-left transition-all hover:bg-white/[0.055] active:scale-[0.99]"
      >
        <span className="grid h-8 w-8 flex-none place-items-center rounded-md bg-accent/15 text-accent">
          <Boxes size={15} strokeWidth={2.2} />
        </span>
        <span className="min-w-0">
          <span className="block text-[12.5px] font-extrabold text-hi">Open Locker</span>
          <span className="block text-[10.5px] font-semibold text-lo">Themes, titles, emblems</span>
        </span>
      </button>
    </div>
  );
}
